import { useCallback, useEffect } from 'react';
import { loginApi } from '../utils/authApi';
import PocketBase from 'pocketbase';

const pb = new PocketBase('https://dbap.a-traq.com');
const SESSION_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds
export function useAuth() {
  // Login function
  const loginUser = useCallback(async (email, password) => {
    const result = await loginApi(email, password);
    if (result && result.token) {
      // Set last activity timestamp
      localStorage.setItem('lastActivity', Date.now().toString());
      // Set up session timeout
      setupSessionTimeout();
    }
    return result;
  }, []);

  // Logout function
  const logoutUser = useCallback(() => {
    // Clear PocketBase auth
    pb.authStore.clear();
    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();
    // Clear any existing timeout
    if (window.sessionTimeout) {
      clearTimeout(window.sessionTimeout);
    }
  }, []);

  // Setup session timeout
  const setupSessionTimeout = useCallback(() => {
    // Clear any existing timeout
    if (window.sessionTimeout) {
      clearTimeout(window.sessionTimeout);
    }

    // Set new timeout
    window.sessionTimeout = setTimeout(() => {
      logoutUser();
      window.location.href = '/login';
    }, SESSION_TIMEOUT);
  }, [logoutUser]);

  // Update last activity timestamp
  const updateLastActivity = useCallback(() => {
    localStorage.setItem('lastActivity', Date.now().toString());
    setupSessionTimeout();
  }, [setupSessionTimeout]);

  // Check session on mount and setup activity listeners
  useEffect(() => {
    const lastActivity = localStorage.getItem('lastActivity');
    if (lastActivity) {
      const timeSinceLastActivity = Date.now() - parseInt(lastActivity);
      if (timeSinceLastActivity >= SESSION_TIMEOUT) {
        // Session has expired
        logoutUser();
        window.location.href = '/login';
      } else {
        // Session is still valid, setup timeout for remaining time
        setupSessionTimeout();
      }
    }

    // Add activity listeners
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    activityEvents.forEach(event => {
      window.addEventListener(event, updateLastActivity);
    });

    return () => {
      // Cleanup listeners
      activityEvents.forEach(event => {
        window.removeEventListener(event, updateLastActivity);
      });
      if (window.sessionTimeout) {
        clearTimeout(window.sessionTimeout);
      }
    };
  }, [setupSessionTimeout, updateLastActivity, logoutUser]);

  return { loginUser, logoutUser };
} 